import { useEffect, useState, useCallback } from 'react';

interface UpdateInfo {
  version: string;
  releaseNotes?: string;
}

interface DownloadProgress {
  percent: number;
  bytesPerSecond: number;
  transferred: number;
  total: number;
}

type UpdateState =
  | 'idle'
  | 'checking'
  | 'available'
  | 'not-available'
  | 'downloading'
  | 'downloaded'
  | 'error';

export function UpdateNotification() {
  const [state, setState] = useState<UpdateState>('idle');
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);
  const [progress, setProgress] = useState<DownloadProgress | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [dismissed, setDismissed] = useState(false);

  const api = (window as any).electronAPI;

  useEffect(() => {
    if (!api) return;

    // Each subscriber returns an unsubscribe fn; collect and run them on cleanup
    // so listeners don't accumulate (which caused duplicate notifications).
    const unsubscribers: Array<(() => void) | undefined> = [
      api.onUpdateChecking?.(() => setState('checking')),
      api.onUpdateAvailable?.((info: UpdateInfo) => {
        setUpdateInfo(info);
        setState('available');
        setDismissed(false);
      }),
      api.onUpdateNotAvailable?.(() => setState('not-available')),
      api.onUpdateDownloadProgress?.((p: DownloadProgress) => {
        setProgress(p);
        setState('downloading');
      }),
      api.onUpdateDownloaded?.((info: UpdateInfo) => {
        setUpdateInfo(info);
        setState('downloaded');
        setDismissed(false);
      }),
      api.onUpdateError?.((err: { message: string }) => {
        setErrorMsg(err.message);
        setState('error');
      }),
    ];

    return () => {
      unsubscribers.forEach((unsub) => { if (typeof unsub === 'function') unsub(); });
    };
  }, []);

  const handleInstall = useCallback(async () => {
    try {
      const result = await api?.installUpdate?.();
      if (result?.success === false) {
        setErrorMsg(result.error || 'Install failed');
        setState('error');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMsg(msg);
      setState('error');
    }
  }, [api]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  const handleCheckNow = useCallback(async () => {
    setState('checking');
    await api?.checkForUpdate?.();
  }, [api]);

  if (dismissed) return null;
  if (state === 'idle' || state === 'not-available') return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '1.25rem',
        right: '1.25rem',
        zIndex: 9999,
        minWidth: 320,
        maxWidth: 400,
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        border: '1px solid rgba(99,102,241,0.35)',
        borderRadius: '0.75rem',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.6)',
        padding: '1rem 1.25rem',
        color: '#f1f5f9',
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '0.875rem',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
          <span style={{ fontSize: '1rem' }}>
            {state === 'checking' && '🔄'}
            {state === 'available' && '🆕'}
            {state === 'downloading' && '⬇️'}
            {state === 'downloaded' && '✅'}
            {state === 'error' && '⚠️'}
          </span>
          <span>
            {state === 'checking' && 'Checking for updates…'}
            {state === 'available' && `Update v${updateInfo?.version} available`}
            {state === 'downloading' && 'Downloading update…'}
            {state === 'downloaded' && `v${updateInfo?.version} ready to install`}
            {state === 'error' && 'Update error'}
          </span>
        </div>
        {state !== 'downloading' && (
          <button
            onClick={handleDismiss}
            style={{
              background: 'none',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              fontSize: '1rem',
              lineHeight: 1,
              padding: '0.125rem 0.25rem',
            }}
            title="Dismiss"
          >
            ×
          </button>
        )}
      </div>

      {/* Body */}
      {state === 'available' && (
        <p style={{ margin: '0 0 0.75rem', color: '#94a3b8', fontSize: '0.8rem' }}>
          A new version is being downloaded in the background.
        </p>
      )}

      {state === 'downloading' && progress && (
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', color: '#94a3b8', fontSize: '0.75rem' }}>
            <span>{progress.percent}%</span>
            <span>{(progress.bytesPerSecond / 1024).toFixed(0)} KB/s</span>
          </div>
          <div style={{ height: '4px', background: '#334155', borderRadius: '2px', overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${progress.percent}%`,
                background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                borderRadius: '2px',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        </div>
      )}

      {state === 'downloaded' && (
        <p style={{ margin: '0 0 0.75rem', color: '#94a3b8', fontSize: '0.8rem' }}>
          Restart the app to apply the update.
        </p>
      )}

      {state === 'error' && (
        <p style={{ margin: '0 0 0.75rem', color: '#f87171', fontSize: '0.8rem' }}>
          {errorMsg}
        </p>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        {state === 'downloaded' && (
          <button
            onClick={handleInstall}
            style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              border: 'none',
              color: '#fff',
              borderRadius: '0.375rem',
              padding: '0.375rem 0.875rem',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.8rem',
            }}
          >
            Restart & Install
          </button>
        )}
        {state === 'error' && (
          <button
            onClick={handleCheckNow}
            style={{
              background: 'rgba(99,102,241,0.15)',
              border: '1px solid rgba(99,102,241,0.4)',
              color: '#a5b4fc',
              borderRadius: '0.375rem',
              padding: '0.375rem 0.875rem',
              cursor: 'pointer',
              fontSize: '0.8rem',
            }}
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}
